import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import useGetDetailGoal from "hooks/api/goals/useGetDetailGoal";
import useGetDetailTarget from "hooks/api/goals/useGetDetailTarget";
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";
import TargetBuilderPanel from "./Targets/TargetBuilderPanel";

/**
 * Route container for the target builder.
 *
 * Two URL shapes are routed here:
 *   - Create:  /clients/:id/goals-targets/:slug/target/new
 *   - Edit:    /clients/:id/goals-targets/:slug/target/:targetId?mode=edit
 *
 * Resolves the parent goal (always) and, in edit mode, the target being edited.
 * Delegates rendering to the standalone `TargetBuilderPanel`. On close — either
 * after a successful save or an explicit discard — the user is sent back to
 * the goal detail view.
 */
const TargetBuilderRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    id: clientId,
    slug: goalIdStr,
    subSlug,
  } = useParams<{
    id: string;
    slug: string;
    subSlug?: string;
  }>();

  const goalId = Number(goalIdStr);
  const isEdit = new URLSearchParams(location.search).get("mode") === "edit";
  const targetId = isEdit && subSlug ? Number(subSlug) : NaN;

  const { goal, isGoalLoading } = useGetDetailGoal(goalId);
  const { target, isTargetLoading } = useGetDetailTarget(Number.isNaN(targetId) ? 0 : targetId);
  const { goals } = useGetListOfGoals(clientId, { includeArchived: false });

  const replacementGoalOptions = goals
    .filter((g) => g.goal_type === "behavior-reduction" && g.id !== goalId)
    .map((g) => ({ id: g.id, title: g.title as string }));

  const replacementBehaviorTargetOptions = (goal?.targets ?? [])
    .filter((t) => Boolean(t.is_replacement_behavior) && (!isEdit || t.id !== target?.id))
    .map((t) => ({ id: t.id, title: t.title }));

  const goBackToGoal = () => {
    history.push(`/clients/${clientId}/goals-targets/${goalId}`);
  };

  if (isGoalLoading || (isEdit && isTargetLoading)) {
    return (
      <Box p={6}>
        <Typography variant="body2" color="text.secondary">
          Loading {isEdit ? "target" : "goal"} context...
        </Typography>
      </Box>
    );
  }

  if (!goal || Number.isNaN(goalId)) {
    return (
      <Box p={6}>
        <Typography variant="body2" color="error.main">
          Goal not found.
        </Typography>
      </Box>
    );
  }

  if (isEdit && (!target || Number.isNaN(targetId))) {
    return (
      <Box p={6}>
        <Typography variant="body2" color="error.main">
          Target not found.
        </Typography>
      </Box>
    );
  }

  return (
    <TargetBuilderPanel
      mode={isEdit ? "edit" : "create"}
      goalId={goalId}
      goalBehaviorSubject={(goal.behavior_subject as "client" | "caregiver") ?? "client"}
      target={isEdit && target ? target : undefined}
      replacementGoalOptions={replacementGoalOptions}
      replacementBehaviorTargetOptions={replacementBehaviorTargetOptions}
      onClose={goBackToGoal}
    />
  );
};

export default TargetBuilderRoute;
